import { useEffect, useMemo, useState } from 'react';

import { debounce, isEqual, pick } from 'lodash';

import {
  keepPreviousData,
  QueryClient,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import { ENTITY_CONFIG, EntityName } from '~system/data/entity-config';

import { buildEntityParams } from '~hooks/api/build-entity-params';

import { filterData } from '~app/utils/filters/filter-data';

export type Meta = {
  total: number;
  page: number;
  limit: number;
  hasNextPage: boolean;
};

export type PaginatedResponse<T> = {
  data: T[];
  meta: Meta;
};

export type QueryOptions<T> = Omit<
  UseQueryOptions<PaginatedResponse<T>, Error, PaginatedResponse<T>, any[]>,
  'queryKey' | 'queryFn' | 'select'
>;

type UseEntityListParams<T> = {
  queryFn: (
    params?: Record<string, any>,
  ) => Promise<T[] | PaginatedResponse<T>>;
  params?: Record<string, any>;
  debounceMs?: number;
  searchBy?: Record<string, string[]>;
  keepPrevious?: boolean;
  queryOptions?: QueryOptions<T>;
};

type UseEntityListResult<T> = Omit<
  UseQueryResult<PaginatedResponse<T>, Error>,
  'data'
> & {
  data: T[];
  meta: Meta;
};

const DEFAULT_LIST_STALE_TIME = 1000 * 60 * 2;

const CLIENT_SCOPE_KEYS = ['scope'] as const;

const CLIENT_REQUEST_KEYS = ['page', 'limit', 'sort', 'sort_by'] as const;

export const getEntityItemId = (item: any) => item?.id ?? item?._id;

export const getEntityItemQueryKey = (entity: EntityName, id?: string) => [
  entity,
  'item',
  id,
];

export const getEntityClientScopeParams = (params?: Record<string, any>) =>
  pick(params ?? {}, CLIENT_SCOPE_KEYS);

export const getEntityClientRequestParams = (params?: Record<string, any>) =>
  pick(params ?? {}, CLIENT_REQUEST_KEYS);

export const getEntityListQueryKey = (
  entity: EntityName,
  isServer: boolean,
  params?: Record<string, any>,
) => {
  if (isServer) {
    return [entity, 'server', params ?? {}];
  }

  return [entity, 'client', getEntityClientScopeParams(params)];
};

export const isEntityClientListQueryKey = (
  queryKey: unknown,
  entity: EntityName,
) => {
  return (
    Array.isArray(queryKey) &&
    queryKey[0] === entity &&
    queryKey[1] === 'client'
  );
};

export const setEntityItemsQueryData = <T>(
  queryClient: QueryClient,
  entity: EntityName,
  items: T[],
) => {
  items.forEach((item) => {
    const id = getEntityItemId(item);

    if (!id) return;

    queryClient.setQueryData(getEntityItemQueryKey(entity, String(id)), item);
  });
};

export const useEntityList = <T>(
  entity: EntityName,
  {
    queryFn,
    params,
    debounceMs = 150,
    searchBy,
    keepPrevious = false,
    queryOptions,
  }: UseEntityListParams<T>,
): UseEntityListResult<T> => {
  const queryClient = useQueryClient();

  const config = ENTITY_CONFIG[entity];
  const isServer = config.strategy === 'server';
  const freshness = 'freshness' in config ? config.freshness : undefined;

  const [effectiveParams, setEffectiveParams] = useState<Record<string, any>>(
    params ?? {},
  );

  const debouncedSetParams = useMemo(
    () =>
      debounce((newParams: Record<string, any>) => {
        setEffectiveParams(newParams);
      }, debounceMs),
    [debounceMs],
  );

  useEffect(() => {
    const nextParams = params ?? {};

    if (isEqual(effectiveParams, nextParams)) return;

    if (isServer) {
      debouncedSetParams.cancel();
      debouncedSetParams(nextParams);
      return;
    }

    setEffectiveParams(nextParams);
  }, [params, isServer, effectiveParams, debouncedSetParams]);

  useEffect(() => {
    return () => debouncedSetParams.cancel();
  }, [debouncedSetParams]);

  const queryKey = getEntityListQueryKey(entity, isServer, effectiveParams);

  const query = useQuery<
    PaginatedResponse<T>,
    Error,
    PaginatedResponse<T>,
    any[]
  >({
    queryKey,

    staleTime: freshness?.staleTime ?? DEFAULT_LIST_STALE_TIME,
    refetchOnWindowFocus: freshness?.refetchOnWindowFocus ?? false,
    refetchOnReconnect: freshness?.refetchOnReconnect ?? false,

    queryFn: async () => {
      const requestParams = isServer
        ? effectiveParams
        : getEntityClientRequestParams(effectiveParams);

      const baseParams = buildEntityParams(isServer, requestParams);
      const result = await queryFn(baseParams);
      const normalized = normalizeResponse(result);

      if (!isServer) {
        setEntityItemsQueryData(queryClient, entity, normalized.data);
      }

      return normalized;
    },

    select: (response) => {
      if (isServer) return response;

      const filtered = filterData<T>({
        data: response.data,
        filters: effectiveParams,
        searchBy,
      });

      return {
        ...response,
        data: filtered,
        meta: {
          ...response.meta,
          total: filtered.length,
          page: 1,
          limit: filtered.length,
          hasNextPage: false,
        },
      };
    },

    placeholderData:
      queryOptions?.placeholderData ??
      (keepPrevious ? keepPreviousData : undefined),

    ...(queryOptions ?? {}),
  });

  const data = query.data?.data ?? [];

  const meta =
    query.data?.meta ??
    ({
      total: data.length,
      page: 1,
      limit: data.length,
      hasNextPage: false,
    } as Meta);

  return {
    ...query,
    data,
    meta,
  };
};

function normalizeResponse<T>(
  result: T[] | PaginatedResponse<T>,
): PaginatedResponse<T> {
  if (Array.isArray(result)) {
    return {
      data: result,
      meta: {
        total: result.length,
        page: 1,
        limit: result.length,
        hasNextPage: false,
      },
    };
  }

  return result;
}
